"use client";

import { useState } from "react";
import { BellIcon, EnvelopeIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";

interface OptInState {
  email: boolean;
  sms: boolean;
}

interface ContactInfo {
  email: string;
  phone: string;
}

export default function NotificationOptIn() {
  const [optIn, setOptIn] = useState<OptInState>({
    email: true,
    sms: false,
  });
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: "",
    phone: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleToggle = (type: keyof OptInState) => {
    setOptIn((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleInputChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <BellIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Varslingsinnstillinger
          </h3>
          <p className="text-sm text-muted-foreground">
            Velg hvordan du vil motta oppdateringer
          </p>
        </div>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800 font-medium">
            ✓ Innstillinger oppdatert
          </p>
        </div>
      )}

      {/* Email Opt-in */}
      <div className="mb-4 p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">E-postvarsler</h4>
              <p className="text-sm text-muted-foreground">
                Motta oppdateringer om registreringer og fornyelser
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle("email")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              optIn.email ? "bg-primary" : "bg-gray-300"
            }`}
            role="switch"
            aria-checked={optIn.email}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                optIn.email ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {optIn.email && (
          <div className="mt-3">
            <label htmlFor="email-input" className="block text-sm font-medium text-foreground mb-1">
              E-postadresse
            </label>
            <input
              id="email-input"
              type="email"
              value={contactInfo.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="din@epost.no"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-white"
            />
          </div>
        )}
      </div>

      {/* SMS Opt-in */}
      <div className="p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <DevicePhoneMobileIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">SMS-varsler</h4>
              <p className="text-sm text-muted-foreground">
                Få viktige påminnelser direkte på mobilen
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle("sms")}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              optIn.sms ? "bg-primary" : "bg-gray-300"
            }`}
            role="switch"
            aria-checked={optIn.sms}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                optIn.sms ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {optIn.sms && (
          <div className="mt-3">
            <label htmlFor="phone-input" className="block text-sm font-medium text-foreground mb-1">
              Telefonnummer
            </label>
            <input
              id="phone-input"
              type="tel"
              value={contactInfo.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="+47 123 45 678"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-white"
            />
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Merk:</strong> Du kan når som helst endre disse innstillingene. Vi sender kun viktige oppdateringer om dine kjøretøy.
        </p>
      </div>
    </div>
  );
}